import { Link } from '@tanstack/react-router';
import type { BookingResponse } from '@/shared/api';
import { cn } from '@/shared/lib/cn';
import type { PropertyLabel } from '../../properties/propertyLabels';
import { agendaCard } from './agendaCardModel';
import { ChannelMark, Facts, NightSegments } from './agendaCardParts';
import { BookingCardMenu } from './BookingCardMenu';

interface Props {
  booking: BookingResponse;
  apartment: PropertyLabel;
  canSeeGuests: boolean;
  today: string;
  /** Kind einer Mehrzimmer-Reservierung: hängt unter dem Gruppenkopf, nur das letzte unten rund. */
  inGroup?: 'middle' | 'last';
}

/** Aufenthaltskarte nach Board „Buchungen“: Apartment, Gast, Zeitraum, Kanal, Betrag; ⋯ für Verwalter. */
export function BookingAgendaCard({ booking, apartment, canSeeGuests, today, inGroup }: Props) {
  const card = agendaCard(booking, canSeeGuests, today);
  const nightsText = `${card.nights} ${card.nights === 1 ? 'Nacht' : 'Nächte'}`;
  return (
    <div className="relative">
      <Link
        to="/bookings/$id"
        params={{ id: booking.id }}
        className={cn(
          'border-hairline text-ink shadow-float grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-x-2.5 gap-y-2 border bg-white p-4 pr-14 no-underline',
          'md:min-h-[76px] md:grid-cols-[124px_minmax(0,1.25fr)_minmax(0,1.2fr)_140px_120px] md:gap-x-4 md:gap-y-1 md:py-3.5 md:pr-16',
          !inGroup
            ? 'rounded-tile'
            : inGroup === 'last'
              ? 'rounded-b-tile border-t-0'
              : 'rounded-none border-t-0',
          card.cancelled && 'bg-paper',
        )}
      >
        <span className="flex min-w-0 flex-col items-start gap-1 md:row-span-2">
          <span className="bg-forest-deep rounded-square inline-flex h-[42px] max-w-full items-center truncate px-3 font-serif text-base font-bold text-white">
            {apartment.name}
          </span>
          {apartment.archived && (
            <span className="text-ink-faint text-[11px] uppercase tracking-wide">archiviert</span>
          )}
        </span>
        <span className="col-span-2 truncate font-serif text-lg font-bold tracking-[-0.01em] md:col-span-1 md:col-start-2 md:row-start-1 md:self-end">
          {card.title}
        </span>
        <span className="col-span-3 flex min-w-0 flex-col gap-1.5 md:col-span-1 md:col-start-3 md:row-span-2 md:row-start-1">
          <span className="flex flex-wrap items-baseline gap-1.5 text-[13px]">
            <b>{card.from}</b>
            <span className="text-ink-faint">→</span>
            <b>{card.to}</b>
            <span className="text-ink-soft">· {nightsText}</span>
          </span>
          <NightSegments
            nights={card.nights}
            done={card.doneNights}
            tone={card.channel.bgClass}
            cancelled={card.cancelled}
          />
          {card.doneNights !== null && (
            <span className="text-forest text-xs font-semibold">
              Nacht {card.doneNights + 1} von {card.nights} · Abreise {card.to}
            </span>
          )}
        </span>
        <span className="flex items-center gap-2 text-[13px] font-semibold md:col-start-4 md:row-span-2 md:row-start-1">
          <ChannelMark
            letter={card.channel.letter}
            className={cn(card.channel.bgClass, card.channel.textClass)}
          />
          <span className="hidden md:inline">{card.channel.label}</span>
        </span>
        <span className="min-w-0 md:col-start-2 md:row-start-2 md:self-start">
          <Facts reference={card.reference} people={card.people} pills={card.pills} />
        </span>
        <span
          className={cn(
            'justify-self-end text-right md:col-start-5 md:row-span-2 md:row-start-1',
            card.priceUnderReview
              ? 'bg-amber-tint rounded-pill px-2.5 py-1 text-xs font-bold'
              : 'font-serif text-[19px] font-bold',
            card.cancelled && 'text-ink-faint line-through',
          )}
        >
          {card.amount}
        </span>
      </Link>
      <BookingCardMenu booking={booking} label={card.title} />
    </div>
  );
}
